import { useState, useEffect } from 'react'
import { progressApi, type ProgressSummary, type WeeklyData, type CalendarData } from '@/services/api'
import StatCards from './StatCards'
import WeeklyChart from './WeeklyChart'
import MonthlyCalendar from './MonthlyCalendar'
import WinsSection from './WinsSection'
import styles from './ProgressDashboard.module.css'
import Icon from '@/components/ui/Icon'

export default function ProgressDashboard() {
  const [summary, setSummary] = useState<ProgressSummary | null>(null)
  const [weekly, setWeekly] = useState<WeeklyData[]>([])
  const [calendar, setCalendar] = useState<CalendarData | null>(null)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [summaryRes, weeklyRes, calendarRes] = await Promise.all([
          progressApi.getSummary(),
          progressApi.getWeekly(),
          progressApi.getCalendar(currentMonth),
        ])
        setSummary(summaryRes.data)
        setWeekly(weeklyRes.data)
        setCalendar(calendarRes.data)
      } catch {
        // Handle error silently
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [currentMonth])

  const handleMonthChange = async (month: string) => {
    setCurrentMonth(month)
    try {
      const res = await progressApi.getCalendar(month)
      setCalendar(res.data)
    } catch {
      // Handle error
    }
  }

  if (loading) {
    return <div className={styles.loading}>Loading progress...</div>
  }

  if (!summary || summary.total === 0) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyIcon}><Icon name="bar-chart-3" size={22} /></span>
        <p className={styles.emptyTitle}>Start your journey!</p>
        <p className={styles.emptyHint}>
          Complete activities to see your progress here
        </p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <StatCards summary={summary} />
      <WeeklyChart data={weekly} />
      <MonthlyCalendar
        data={calendar}
        currentMonth={currentMonth}
        onMonthChange={handleMonthChange}
      />
      <WinsSection summary={summary} />
    </div>
  )
}
